import { useContext } from 'react';
import { Col } from 'react-bootstrap';
import useAppUser from 'hooks/useAppUser';
import useFormatCurrency from 'hooks/useFormatCurrency';
import useFormatNumber from 'hooks/useFormatNumber';
import AnalyticsContext from 'pages/analyticsAndHostQuality/AnalyticsContext';
import usePercentageKeyMetricsData from '../usePercentageKeyMetricsData';
import useKeyMetricsData from '../useKeyMetricsData';
import { isCardDataApplicable } from '../Analytics.utils';
import { AnalyticsKeyMetric } from '../types/AnalyticsKeyMetrics.types';
import { AnalyticsListWrapper } from './AnalyticsList.styles';
import AnalyticsListItem from './AnalyticsListItem';

const AnalyticsList = () => {
  const { isOwner } = useAppUser();

  const { keyMetrics, isKeyMetricsLoading, keyMetricsError } =
    useKeyMetricsData();
  const { percentageKeyMetrics, isPercentageKeyMetricsLoading } =
    usePercentageKeyMetricsData();
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const { filter } = useContext(AnalyticsContext);

  const rentalRevenueMetric = isOwner
    ? AnalyticsKeyMetric.OWNER_REVENUE
    : AnalyticsKeyMetric.RENTAL_REVENUE;
  const rentalRevenue = keyMetrics?.find(
    (km) => km.metric === rentalRevenueMetric,
  );
  const rentalRevenuePercentage = percentageKeyMetrics?.find(
    (km) => km.metric === rentalRevenueMetric,
  );

  const nightsBooked = keyMetrics?.find(
    (km) => km.metric === AnalyticsKeyMetric.NIGHTS_BOOKED,
  );
  const nightsBookedPercentage = percentageKeyMetrics?.find(
    (km) => km.metric === AnalyticsKeyMetric.NIGHTS_BOOKED,
  );

  const occupancyRate = keyMetrics?.find(
    (km) => km.metric === AnalyticsKeyMetric.OCCUPANCY_RATE,
  );
  const occupancyRatePercentage = percentageKeyMetrics?.find(
    (km) => km.metric === AnalyticsKeyMetric.OCCUPANCY_RATE,
  );

  const avgDailyRate = keyMetrics?.find(
    (km) => km.metric === AnalyticsKeyMetric.AVERAGE_DAILY_RATE,
  );
  const avgDailyRatePercentage = percentageKeyMetrics?.find(
    (km) => km.metric === AnalyticsKeyMetric.AVERAGE_DAILY_RATE,
  );

  const revPar = keyMetrics?.find(
    (km) => km.metric === AnalyticsKeyMetric.REV_PAR,
  );
  const revParPercentage = percentageKeyMetrics?.find(
    (km) => km.metric === AnalyticsKeyMetric.REV_PAR,
  );

  return (
    <Col xs={12}>
      <AnalyticsListWrapper>
        <AnalyticsListItem
          backgroundColor="#3a84d81a"
          labelKey="rentalRevenue"
          helpKey="rentalRevenueHelp"
          // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          value={
            rentalRevenue &&
            formatCurrency({
              value: rentalRevenue.value,
              // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              currency: rentalRevenue.currency,
              options: {
                round: true,
                notation: 'compact',
                removeDecimals: rentalRevenue.value < 1000,
              },
            })
          }
          percentage={
            rentalRevenuePercentage && rentalRevenuePercentage.percentage
          }
          notApplicable={
            !isCardDataApplicable(filter, AnalyticsKeyMetric.RENTAL_REVENUE)
          }
          error={keyMetricsError}
          isLoading={isKeyMetricsLoading}
          isPercentageLoading={isPercentageKeyMetricsLoading}
        />

        <AnalyticsListItem
          backgroundColor="#ffc4541a"
          labelKey="nightsBooked"
          helpKey="nightsBookedHelp"
          // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          value={
            nightsBooked &&
            formatNumber({
              value: nightsBooked.value,
              options: { notation: 'compact' },
            })
          }
          percentage={
            nightsBookedPercentage && nightsBookedPercentage.percentage
          }
          notApplicable={
            !isCardDataApplicable(filter, AnalyticsKeyMetric.NIGHTS_BOOKED)
          }
          error={keyMetricsError}
          isLoading={isKeyMetricsLoading}
          isPercentageLoading={isPercentageKeyMetricsLoading}
        />
        <AnalyticsListItem
          backgroundColor="#31b48d1a"
          labelKey="occupancyRate"
          helpKey="occupancyRateHelp"
          // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          value={
            occupancyRate &&
            `${formatNumber({ value: occupancyRate.value * 100 })}%`
          }
          notApplicable={
            !isCardDataApplicable(filter, AnalyticsKeyMetric.OCCUPANCY_RATE)
          }
          percentage={
            occupancyRatePercentage && occupancyRatePercentage.percentage
          }
          error={keyMetricsError}
          isLoading={isKeyMetricsLoading}
          isPercentageLoading={isPercentageKeyMetricsLoading}
        />
        <AnalyticsListItem
          data-testid="analytis-list-item-daily"
          backgroundColor="#8a5bda1a"
          labelKey="avgDailyRate"
          helpKey="avgDailyRateHelp"
          // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          value={
            avgDailyRate &&
            formatCurrency({
              value: avgDailyRate.value,
              // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              currency: avgDailyRate.currency,
              options: {
                round: true,
                notation: 'compact',
                removeDecimals: avgDailyRate.value < 1000,
              },
            })
          }
          percentage={
            avgDailyRatePercentage && avgDailyRatePercentage.percentage
          }
          notApplicable={
            !isCardDataApplicable(filter, AnalyticsKeyMetric.AVERAGE_DAILY_RATE)
          }
          error={keyMetricsError}
          isLoading={isKeyMetricsLoading}
          isPercentageLoading={isPercentageKeyMetricsLoading}
        />
        <AnalyticsListItem
          backgroundColor="#ff72541a"
          labelKey="revPar"
          helpKey="revParHelp"
          // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          value={
            revPar &&
            formatCurrency({
              value: revPar.value,
              // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              currency: revPar.currency,
              options: {
                round: true,
                notation: 'compact',
                removeDecimals: revPar.value < 1000,
              },
            })
          }
          percentage={revParPercentage && revParPercentage.percentage}
          notApplicable={
            !isCardDataApplicable(filter, AnalyticsKeyMetric.REV_PAR)
          }
          error={keyMetricsError}
          isLoading={isKeyMetricsLoading}
          isPercentageLoading={isPercentageKeyMetricsLoading}
        />
      </AnalyticsListWrapper>
    </Col>
  );
};

export default AnalyticsList;
